import { AccountSelector, AnimatedList, MathInput, TextTooltip, VatSelect } from '@components'
import {
  BlueprintType,
  ACC_TYPES_PURCHASE,
  ACC_TYPES_SALES,
  ACC_TYPES_SALES_PAYMENT,
  ACC_TYPES_PURCHASE_PAYMENT,
  VatCode
} from '@constants'
import { isVATObliged, useBusinessContext } from '@context'
import { DefaultQueryParams, fetchAccount } from '@query'
import { formatCurrency } from '@utils'
import cn from 'classnames'
import { AnimatePresence } from 'framer-motion'
import React, { useCallback, useEffect } from 'react'
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaFeatherAlt, FaPlus, FaQuestionCircle } from 'react-icons/fa'
import {
  AnimatedWarningIconWrapper,
  DeleteIcon,
  EntryCol,
  EntryRow,
  EntryWrapper,
  Header,
  PaymentMethodWrapper,
  StyledButton,
  StyledTotalAmount
} from './DocumentEntrySection.styled'
import {
  useBlueprintAccountEffect,
  useBlueprintDebetCreditDifference,
  useEntryUpdater
} from './hooks'
import { getVatRate } from '@utils/getVatOptions.tsx'
import { useVatOptions } from '@root/hooks/useVatOptions'
import { useQueryClient } from '@tanstack/react-query'

interface BlueprintEntryProps {
  dateAt?: string
  baseName: string
  showDescriptions?: boolean
  accountFilter: DefaultQueryParams | null
  vatChoices: 'all' | 'purchase' | 'sales'
  blueprintType: BlueprintType
  accountLabel: string
  accountHint?: string
  buttonLabel: string
  buttonIcon: React.ReactElement
  hideFooter?: boolean
  minEntries?: number
  maxEntries?: number
  showTotalAmount?: boolean

  expectedPaymentAccount?: number
  expectedPaymentAccountAmount?: number
}

interface TotalAmountProps {
  baseName: string

  expectedPaymentAccount?: number
  expectedPaymentAccountAmount?: number
}

const createDefaultEntryData = () => ({
  account_id: null,
  amount: 0,
  description: null,
  vat_code: VatCode.TAX_FREE,
  vat_rate: 0
})

const parseError = (errors, fieldPath: string) => {
  let error = errors
  const parts = fieldPath.split('.')
  for (const part of parts) {
    error = error[part]
    if (!error) break
  }
  return error
}

const TotalAmount: React.FC<TotalAmountProps> = ({
  baseName,
  expectedPaymentAccount,
  expectedPaymentAccountAmount
}) => {
  const [t] = useTranslation()

  const { control } = useFormContext()

  const totalAmount = useWatch({ control, name: baseName })?.reduce(
    (current, { amount }) => (amount ? parseFloat(amount) : 0) + current,
    0
  )

  const formattedTotalAmount = totalAmount
    ? formatCurrency(Math.round(totalAmount * 100) / 100)
    : formatCurrency(0)

  const calculatedPaymentAccountAmount = useBlueprintAccountEffect(expectedPaymentAccount)

  const debetCreditDiff = useBlueprintDebetCreditDifference()

  const showDebetCreditDiffWarning =
    debetCreditDiff !== null &&
    debetCreditDiff !== 0 &&
    expectedPaymentAccount == null &&
    expectedPaymentAccountAmount == null

  const showExpectedTotalAmountWarning =
    totalAmount !== 0 && expectedPaymentAccountAmount
      ? Math.round(expectedPaymentAccountAmount * 100) !==
        Math.round(calculatedPaymentAccountAmount * 100)
      : false

  const hasWarnings = showDebetCreditDiffWarning || showExpectedTotalAmountWarning

  const warningsToShow = [
    {
      visible: showExpectedTotalAmountWarning,
      key: 'expectedTotalAmountWarning',
      toolTip: t('document.blueprint.totalDoesNotMatchExpected', {
        expectedAmount: formatCurrency(expectedPaymentAccountAmount)
      })
    },
    {
      visible: showDebetCreditDiffWarning,
      key: 'debetCreditDiffWarning',
      toolTip: t('document.blueprint.debetCreditDifference', {
        difference: formatCurrency(debetCreditDiff)
      })
    }
  ].filter(warning => warning.visible)

  return (
    <div>
      <StyledTotalAmount className={cn({ warning: hasWarnings })}>
        <AnimatePresence>
          {warningsToShow.map(warning => (
            <AnimatedWarningIconWrapper key={warning.key}>
              <TextTooltip tooltip={warning.toolTip} placement="bottom-start">
                <FaQuestionCircle className="warning-icon" />
              </TextTooltip>
            </AnimatedWarningIconWrapper>
          ))}
        </AnimatePresence>
        <span className="title">{t('document.blueprint.total')}</span>
        <span className="amount">{formattedTotalAmount}</span>
      </StyledTotalAmount>
    </div>
  )
}

const BlueprintEntryEditor: React.FC<BlueprintEntryProps> = ({
  baseName,
  accountFilter,
  vatChoices,
  accountLabel,
  accountHint,
  buttonLabel,
  buttonIcon,
  minEntries = 1,
  maxEntries = 999,
  showTotalAmount = true,
  expectedPaymentAccount,
  expectedPaymentAccountAmount
}) => {
  const queryClient = useQueryClient()
  const [t] = useTranslation()
  const { slug, data: business } = useBusinessContext()
  const {
    control,
    setFocus,
    setValue,
    getValues,
    formState: { errors }
  } = useFormContext()
  const vat_obliged = isVATObliged(business)
  const { fields, append, remove } = useFieldArray({
    control,
    name: baseName,
    shouldUnregister: false,
    rules: { minLength: 1 }
  })

  const { updateEntryAmountsWhenAddingOrChangingEntry, updateEntryAmountsWhenEntryIsRemoved } =
    useEntryUpdater(setValue)

  // According to the docs the append, insert
  // and update must always use non partial default values.
  const customAppend = useCallback(() => {
    append(createDefaultEntryData(), { shouldFocus: false })
    updateEntryAmountsWhenAddingOrChangingEntry(
      baseName,
      getValues('blueprint'),
      getValues('blueprint_type')
    )
  }, [append, baseName, getValues, updateEntryAmountsWhenAddingOrChangingEntry])

  const customRemove = (index: number, baseName: string) => {
    remove(index)

    // It is not recommended to stack useFieldArray and useForm actions.
    // Use setTimeout to debounce the update done by the hook after removal.
    // https://github.com/react-hook-form/react-hook-form/issues/12597
    // Without this, the UI will not update correctly.
    setTimeout(() => {
      updateEntryAmountsWhenEntryIsRemoved(
        baseName,
        getValues('blueprint'),
        getValues('blueprint_type')
      )
    }, 0)
  }

  const formDate = useWatch({
    control,
    name: 'date'
  })
  const vatOptions = useVatOptions(business?.country_config, formDate)

  // Make sure we have at least one entry
  useEffect(() => {
    const missingFields = minEntries - fields.length
    if (missingFields <= 0) return

    for (let i = 0; i < missingFields; i += 1) {
      customAppend()
    }
  }, [fields.length, minEntries, customAppend])

  const maxEntriesExceeded = fields.length >= maxEntries

  const deleteItem = (index: number) => {
    if (fields.length > minEntries) {
      customRemove(index, baseName)
    }
  }

  const updateVATSelection = useCallback(
    (accountId: number, index: number) => {
      if (!vat_obliged) return

      const commonOpts = {
        shouldDirty: true,
        shouldTouch: true
      }

      if (accountId) {
        queryClient
          .fetchQuery({
            queryKey: [slug, 'accounts', accountId],
            queryFn: () => fetchAccount({ slug, accountId })
          })
          .then(result => {
            const vatRate = getVatRate(
              business.country_config,
              result?.default_vat_rate_label,
              formDate
            )
            setValue(`${baseName}.${index}.vat_rate`, vatRate, commonOpts)
            setValue(`${baseName}.${index}.vat_code`, result?.default_vat_code, commonOpts)
          })
      }
      // Clear the VAT code and rate if the account is removed
      else {
        setValue(`${baseName}.${index}.vat_rate`, '', commonOpts)
        setValue(`${baseName}.${index}.vat_code`, '', commonOpts)
      }
    },
    [business?.id, setValue, formDate, baseName, vat_obliged, queryClient]
  )

  return (
    <AnimatedList
      items={fields.map((entry, index) => (
        <EntryWrapper key={baseName + entry.id}>
          <EntryCol style={{ flex: 1 }}>
            <EntryRow>
              <div className="account-select-wrapper">
                <Controller
                  control={control}
                  name={`${baseName}.${index}.account_id`}
                  rules={{ required: { value: true, message: t('validation.required') } }}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <AccountSelector
                        {...field}
                        ref={null}
                        required={true}
                        errors={error}
                        accountFilter={accountFilter}
                        label={accountLabel}
                        info={accountHint}
                        id={`${baseName}.${index}.account_id`}
                        onChange={accountId => {
                          field.onChange(accountId)
                          updateVATSelection(accountId, index)
                        }}
                      ></AccountSelector>
                    </>
                  )}
                />
              </div>

              {vat_obliged && business && (
                <div className="vat-select-wrapper">
                  <VatSelect
                    id={`${baseName}.${index}.vat_select`}
                    control={control}
                    include={vatChoices}
                    vatCodeName={`${baseName}.${index}.vat_code`}
                    vatRateName={`${baseName}.${index}.vat_rate`}
                    isInvalid={Boolean(parseError(errors, `${baseName}.${index}.vat_rate`))}
                    options={vatOptions}
                  />
                </div>
              )}

              <div className="amount-wrapper">
                <Controller
                  control={control}
                  name={`${baseName}.${index}.amount`}
                  defaultValue={0}
                  rules={{
                    required: { value: true, message: t('validation.requiredShort') },
                    validate: {
                      isValid: value => {
                        const MIN_VALUE = 0
                        const MAX_VALUE = 9999999999

                        if (value === '' || value === null) {
                          return t('validation.requiredShort')
                        }

                        if (value < MIN_VALUE) return `< ${MIN_VALUE}`
                        if (value > MAX_VALUE) return `> ${MAX_VALUE}`

                        return true
                      }
                    }
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <MathInput
                      {...field}
                      data-test="document-form-amount"
                      id={`${baseName}.${index}.amount`}
                      label={t('document.blueprint.amount')}
                      errors={error}
                      required={true}
                      info={t('document.blueprint.amountInfo')}
                      step={'0.01'}
                      onChange={e => {
                        field.onChange(e)
                        updateEntryAmountsWhenAddingOrChangingEntry(
                          baseName,
                          getValues('blueprint'),
                          getValues('blueprint_type')
                        )
                      }}
                    />
                  )}
                />
              </div>
            </EntryRow>
          </EntryCol>
          <EntryCol>
            <EntryRow>
              <DeleteIcon
                $disabled={fields.length <= minEntries}
                onClick={() => deleteItem(index)}
              />
            </EntryRow>
          </EntryCol>
        </EntryWrapper>
      ))}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
          {!maxEntriesExceeded && (
            <StyledButton
              type="button"
              key="add-entry-button"
              isSecondary={true}
              size="sm"
              data-test="add-sales-expense-button"
              icon={buttonIcon}
              onClick={() => {
                customAppend()
                setFocus(`${baseName}.${fields.length}.account_id`)
              }}
            >
              {buttonLabel}
            </StyledButton>
          )}
          {showTotalAmount && (
            <TotalAmount
              baseName={baseName}
              expectedPaymentAccount={expectedPaymentAccount}
              expectedPaymentAccountAmount={expectedPaymentAccountAmount}
            />
          )}
        </div>
      }
    />
  )
}

interface BlueprintEditorProps {
  contactId?: number
  expectedPaymentAccount?: number
  expectedPaymentAccountAmount?: number
}

function entryAccountFilter(
  base: DefaultQueryParams | null,
  contactId?: number
): DefaultQueryParams | null {
  const contactPart = contactId != null ? { contact_id: contactId } : {}
  if (base == null) return contactId != null ? contactPart : null
  return { ...base, ...contactPart }
}

export const BlueprintEditor: React.FC<BlueprintEditorProps> = ({
  contactId,
  expectedPaymentAccount,
  expectedPaymentAccountAmount
}) => {
  const [t] = useTranslation()
  const { control } = useFormContext()
  const blueprintType = useWatch({
    control,
    name: 'blueprint_type',
    defaultValue: BlueprintType.PURCHASE
  })

  switch (blueprintType) {
    case BlueprintType.PURCHASE: {
      return (
        <div key={BlueprintType.PURCHASE}>
          <PaymentMethodWrapper>
            <Controller
              control={control}
              name={'blueprint.credit_account_id'}
              rules={{ required: { value: true, message: t('validation.required') } }}
              render={({ field, fieldState: { error } }) => (
                <AccountSelector
                  {...field}
                  ref={null}
                  required={true}
                  errors={error}
                  accountFilter={entryAccountFilter(
                    { type: ACC_TYPES_PURCHASE_PAYMENT },
                    contactId
                  )}
                  label={t('document.blueprint.paymentMethodAccount')}
                  info={t('document.blueprint.paymentMethodAccountInfoPurchase')}
                  queryKey="blueprint-purchase"
                ></AccountSelector>
              )}
            />
          </PaymentMethodWrapper>

          <Header>{t('document.blueprint.purchases')}</Header>
          <BlueprintEntryEditor
            blueprintType={blueprintType}
            baseName={'blueprint.debet_entries'}
            accountFilter={entryAccountFilter({ type: ACC_TYPES_PURCHASE }, contactId)}
            vatChoices="purchase"
            accountLabel={t('document.blueprint.account')}
            buttonLabel={t('document.blueprint.addEntry')}
            buttonIcon={<FaPlus />}
            expectedPaymentAccount={expectedPaymentAccount}
            expectedPaymentAccountAmount={expectedPaymentAccountAmount}
          />
        </div>
      )
    }
    case BlueprintType.SALES: {
      return (
        <div key={BlueprintType.SALES}>
          <PaymentMethodWrapper>
            <Controller
              control={control}
              name={`blueprint.debet_account_id`}
              rules={{ required: { value: true, message: t('validation.required') } }}
              render={({ field, fieldState: { error } }) => (
                <AccountSelector
                  {...field}
                  ref={null}
                  required={true}
                  errors={error}
                  accountFilter={entryAccountFilter({ type: ACC_TYPES_SALES_PAYMENT }, contactId)}
                  label={t('document.blueprint.paymentMethodAccount')}
                  info={t('document.blueprint.paymentMethodAccountInfoSales')}
                  queryKey="blueprint-sales"
                ></AccountSelector>
              )}
            />

            <BlueprintEntryEditor
              blueprintType={blueprintType}
              baseName={'blueprint.expense_entries'}
              accountFilter={entryAccountFilter({ type: ACC_TYPES_PURCHASE }, contactId)}
              vatChoices="purchase"
              accountLabel={t('document.blueprint.expenseAccount')}
              accountHint={t('document.blueprint.expenseAccountHint')}
              buttonLabel={t('document.blueprint.addExpense')}
              buttonIcon={<FaFeatherAlt />}
              minEntries={0}
              maxEntries={1}
              showTotalAmount={false}
            />
          </PaymentMethodWrapper>

          <Header>{t('document.blueprint.sales')}</Header>
          <BlueprintEntryEditor
            blueprintType={blueprintType}
            baseName={'blueprint.credit_entries'}
            accountFilter={entryAccountFilter({ type: ACC_TYPES_SALES }, contactId)}
            vatChoices="sales"
            accountLabel={t('document.blueprint.account')}
            buttonLabel={t('document.blueprint.addEntry')}
            buttonIcon={<FaPlus />}
            expectedPaymentAccount={expectedPaymentAccount}
            expectedPaymentAccountAmount={expectedPaymentAccountAmount}
          />
        </div>
      )
    }
    default: {
      return (
        <div key={BlueprintType.MANUAL}>
          <Header>{t('document.blueprint.debet')}</Header>
          <BlueprintEntryEditor
            blueprintType={blueprintType}
            baseName={'blueprint.debet_entries'}
            accountFilter={entryAccountFilter(null, contactId)}
            vatChoices="all"
            accountLabel={t('document.blueprint.account')}
            buttonLabel={t('document.blueprint.addEntry')}
            buttonIcon={<FaPlus />}
            expectedPaymentAccount={expectedPaymentAccount}
            expectedPaymentAccountAmount={expectedPaymentAccountAmount}
          />

          <br />

          <Header>{t('document.blueprint.credit')}</Header>
          <BlueprintEntryEditor
            blueprintType={blueprintType}
            baseName={'blueprint.credit_entries'}
            accountFilter={entryAccountFilter(null, contactId)}
            vatChoices="all"
            accountLabel={t('document.blueprint.account')}
            buttonLabel={t('document.blueprint.addEntry')}
            buttonIcon={<FaPlus />}
            expectedPaymentAccount={expectedPaymentAccount}
            expectedPaymentAccountAmount={expectedPaymentAccountAmount}
          />
        </div>
      )
    }
  }
}
